/**
 * O ecossistema do FluxFin — o mapa que abre a documentação.
 *
 * Existe porque a documentação começava pelo problema, em prosa, e quem abre
 * uma ferramenta pela primeira vez quer antes de tudo saber **o que ela
 * faz** e **como as partes se ligam**. Um parágrafo responde isso devagar;
 * um mapa responde de uma olhada.
 *
 * Desenho em SVG, e não em caixas de HTML, por dois motivos: as **ligações**
 * são o conteúdo — é delas que se entende que o protocolo só existe depois
 * do Bioslab — e um `viewBox` escala o conjunto inteiro sem reposicionar
 * nada no celular.
 *
 * As cores saem dos tokens do sistema. Cada etapa é clicável e leva à tela
 * correspondente: o mapa também serve de índice.
 */
import { useNavigate } from 'react-router-dom';

/* As quatro faixas do fluxo, na ordem em que o dinheiro percorre o sistema.
   O `y` é o centro vertical de cada faixa no viewBox. */
const FAIXAS = [
  { y: 92, rotulo: 'Entrada' },
  { y: 232, rotulo: 'Apuração' },
  { y: 372, rotulo: 'Conferência' },
  { y: 496, rotulo: 'Saída' },
];

const NOS = [
  {
    id: 'modelos',
    x: 120,
    y: 92,
    titulo: 'Modelos de arquivo',
    linha: 'a IA aprende a ler',
    icone: '', // bi-robot
    rota: '/modelos',
    tom: 'ia',
  },
  {
    id: 'upload',
    x: 400,
    y: 92,
    titulo: 'Lançar recebimento',
    linha: 'PDF, planilha ou XML',
    icone: '', // bi-upload
    rota: '/arquivos/novo',
    tom: 'entrada',
  },
  {
    id: 'agenda',
    x: 680,
    y: 92,
    titulo: 'Agenda de envios',
    linha: 'o que ainda não chegou',
    icone: '', // bi-calendar3
    rota: '/agenda',
    tom: 'alerta',
  },

  {
    id: 'extracao',
    x: 120,
    y: 232,
    titulo: 'Extração',
    linha: 'guia · valor · procedimento',
    icone: '', // bi-file-earmark-text
    rota: '/arquivos',
    tom: 'neutro',
  },
  {
    id: 'conferencia',
    x: 400,
    y: 232,
    titulo: 'Conferência',
    linha: 'bate com o total do documento',
    icone: '', // bi-check2-circle
    rota: '/arquivos',
    tom: 'ok',
  },
  {
    id: 'bioslab',
    x: 680,
    y: 232,
    titulo: 'Bioslab',
    linha: 'guia → protocolo',
    icone: '', // bi-diagram-3
    rota: '/pendencias',
    tom: 'bioslab',
  },

  {
    id: 'pendencias',
    x: 120,
    y: 372,
    titulo: 'Pendências',
    linha: 'guia que não achou protocolo',
    icone: '', // bi-exclamation-triangle
    rota: '/pendencias',
    tom: 'alerta',
  },
  {
    id: 'divergencias',
    x: 400,
    y: 372,
    titulo: 'Divergências',
    linha: 'cobrado × pago, sem julgar',
    icone: '', // bi-arrow-left-right
    rota: '/divergencias',
    tom: 'neutro',
  },
  {
    id: 'glosa',
    x: 680,
    y: 372,
    titulo: 'Glosa e auditoria',
    linha: 'o que foi recusado, e por quê',
    icone: '', // bi-scissors
    rota: '/glosas',
    tom: 'glosa',
  },

  // A faixa de saída tem DUAS respostas, não uma. "Baixa por protocolo" diz
  // o que lançar agora; "Saldo a receber" diz o que ainda falta entrar. Pôr
  // as duas lado a lado é o que impede confundir uma com a outra.
  {
    id: 'protocolo',
    x: 260,
    y: 496,
    titulo: 'Baixa por protocolo',
    linha: 'o número que vai ao Nasajon',
    icone: '', // bi-cash-stack
    rota: '/protocolos',
    tom: 'destaque',
  },
  {
    id: 'saldo',
    x: 540,
    y: 496,
    titulo: 'Saldo a receber',
    linha: 'cobrado − pago − glosado',
    icone: '', // bi-wallet2
    rota: '/saldo',
    tom: 'destaque',
  },
];

/* As ligações são o conteúdo: é delas que se lê que o protocolo só existe
   depois do Bioslab, e que a glosa explica parte do que não entrou. */
const LIGACOES = [
  ['modelos', 'upload'],
  ['upload', 'extracao'],
  ['extracao', 'conferencia'],
  ['conferencia', 'bioslab'],
  ['bioslab', 'pendencias'],
  ['bioslab', 'protocolo'],
  ['conferencia', 'divergencias'],
  ['bioslab', 'divergencias'],
  ['upload', 'glosa'],
  ['glosa', 'protocolo'],
  ['upload', 'agenda'],
  // O saldo nasce do cruzamento dos três: o total cobrado vem do Bioslab, o
  // recebido da baixa e o glosado do extrato.
  ['bioslab', 'saldo'],
  ['glosa', 'saldo'],
  ['protocolo', 'saldo'],
];

const TONS = {
  ia: { borda: '#7c5cff', fundo: 'rgba(124,92,255,.10)' },
  entrada: { borda: '#1f7ae0', fundo: 'rgba(31,122,224,.10)' },
  neutro: { borda: '#64748b', fundo: 'rgba(100,116,139,.09)' },
  ok: { borda: '#0a9b63', fundo: 'rgba(10,155,99,.10)' },
  bioslab: { borda: '#0ea5a5', fundo: 'rgba(14,165,165,.10)' },
  alerta: { borda: '#d9a520', fundo: 'rgba(217,165,32,.12)' },
  glosa: { borda: '#e0533d', fundo: 'rgba(224,83,61,.10)' },
  destaque: { borda: '#0b2a4a', fundo: 'rgba(11,42,74,.08)' },
};

const L = 240; // largura padrão do nó
const A = 68; // altura

function centro(no) {
  return { x: no.x + (no.largura || L) / 2, y: no.y + A / 2 };
}

function Caminho({ de, para }) {
  const a = centro(de);
  const b = centro(para);
  const mesmaFaixa = de.y === para.y;

  // Mesma faixa: reta. Faixas diferentes: curva em S, que deixa claro que o
  // fluxo desceu de etapa em vez de ser uma ligação lateral qualquer.
  const d = mesmaFaixa
    ? `M ${a.x + (de.largura || L) / 2 - 4} ${a.y} L ${b.x - (para.largura || L) / 2 + 4} ${b.y}`
    : `M ${a.x} ${de.y + A} C ${a.x} ${de.y + A + 46}, ${b.x} ${para.y - 46}, ${b.x} ${para.y - 4}`;

  return (
    <path
      d={d}
      fill="none"
      stroke="url(#ff-fluxo)"
      strokeWidth="2"
      strokeLinecap="round"
      markerEnd="url(#ff-seta)"
      opacity="0.75"
    />
  );
}

function No({ no, aoClicar }) {
  const tom = TONS[no.tom] || TONS.neutro;
  const largura = no.largura || L;

  return (
    <g
      transform={`translate(${no.x} ${no.y})`}
      style={{ cursor: 'pointer' }}
      onClick={() => aoClicar(no.rota)}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && aoClicar(no.rota)}
      aria-label={`${no.titulo} — ${no.linha}`}
    >
      <rect
        width={largura}
        height={A}
        rx="14"
        fill={tom.fundo}
        stroke={tom.borda}
        strokeWidth="1.5"
      />
      {/* A barra lateral dá a cor da etapa sem tingir o texto. */}
      <rect width="4" height={A - 24} y="12" x="0" rx="2" fill={tom.borda} />

      <text
        x="20"
        y="28"
        fontFamily="bootstrap-icons"
        fontSize="17"
        fill={tom.borda}
      >
        {no.icone}
      </text>
      <text x="46" y="29" fontSize="14.5" fontWeight="600" fill="var(--ff-texto, #0b2a4a)">
        {no.titulo}
      </text>
      <text x="20" y="50" fontSize="12" fill="var(--ff-muted, #64748b)">
        {no.linha}
      </text>
    </g>
  );
}

export default function Ecossistema() {
  const navigate = useNavigate();
  const porId = Object.fromEntries(NOS.map((n) => [n.id, n]));

  return (
    <figure style={{ margin: '0 0 24px' }}>
      <div
        style={{
          border: '1px solid var(--ff-border-soft)',
          borderRadius: 16,
          background:
            'radial-gradient(1200px 400px at 15% -10%, rgba(31,122,224,.07), transparent 60%),' +
            'radial-gradient(900px 380px at 90% 110%, rgba(10,155,99,.07), transparent 60%),' +
            'var(--ff-surface, #fff)',
          padding: '8px 4px',
          overflowX: 'auto',
        }}
      >
        <svg
          viewBox="0 0 960 600"
          width="100%"
          style={{ minWidth: 680, display: 'block' }}
          role="img"
          aria-label="Como as partes do FluxFin se ligam, da leitura do arquivo à baixa por protocolo"
        >
          <defs>
            <linearGradient id="ff-fluxo" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#1f7ae0" />
              <stop offset="100%" stopColor="#0a9b63" />
            </linearGradient>
            <marker
              id="ff-seta"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="5"
              markerHeight="5"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#0a9b63" opacity="0.8" />
            </marker>
          </defs>

          {/* As faixas primeiro, no fundo: elas nomeiam o estágio sem
              competir com os nós. */}
          {FAIXAS.map((f) => (
            <g key={f.rotulo}>
              <line
                x1="24"
                y1={f.y - 24}
                x2="936"
                y2={f.y - 24}
                stroke="var(--ff-border-soft, #e2e8f0)"
                strokeDasharray="3 6"
              />
              <text
                x="24"
                y={f.y - 30}
                fontSize="10.5"
                letterSpacing="1.4"
                fill="var(--ff-muted, #94a3b8)"
              >
                {f.rotulo.toUpperCase()}
              </text>
            </g>
          ))}

          {LIGACOES.map(([a, b]) => (
            <Caminho key={`${a}-${b}`} de={porId[a]} para={porId[b]} />
          ))}

          {NOS.map((n) => (
            <No key={n.id} no={n} aoClicar={navigate} />
          ))}
        </svg>
      </div>

      <figcaption
        style={{ fontSize: 13, color: 'var(--ff-muted)', marginTop: 10, lineHeight: 1.55 }}
      >
        Cada caixa é uma tela do sistema — clique para ir até ela. As setas são a ordem
        em que as coisas acontecem: nada vira <strong>baixa por protocolo</strong> sem
        antes passar pela conferência aritmética e pelo Bioslab, e é por isso que o
        número final pode ser defendido.
      </figcaption>
    </figure>
  );
}
